import { type Product, type SaleMode, getPortionPrice } from '@paulownia/shared/types';

/**
 * Calculate estimated weight in kg based on sale mode and selection
 * Returns null if weight is not estimated (e.g., UNIT, WEIGHT_PACKAGED)
 */
export function calculateEstimatedWeight(
    product: Product,
    saleMode: SaleMode,
    quantity: number,
    servings: number
): number | null {
    switch (saleMode) {
        case 'UNIT_PRICED_BY_WEIGHT': {
            const avgWeight = product.weightEstimate?.averageWeight;
            if (!avgWeight) return null;
            return avgWeight * quantity;
        }

        case 'WEIGHT':
            return quantity; // quantity is already in kg

        case 'WEIGHT_ESTIMATED': {
            const gramsPerPerson = product.servingEstimate?.gramsPerPerson;
            if (!gramsPerPerson) return null;
            return (gramsPerPerson * servings) / 1000; // Convert to kg
        }

        // UNIT and WEIGHT_PACKAGED don't have variable weight
        case 'UNIT':
        case 'WEIGHT_PACKAGED':
        default:
            return null;
    }
}

/**
 * Calculate estimated price based on sale mode and selection
 */
export function calculateEstimatedPrice(
    product: Product,
    saleMode: SaleMode,
    quantity: number,
    servings: number,
    selectedPortionSizeId: string | null
): number | null {
    const price = product.price;
    if (price === undefined || price === null) return null;

    switch (saleMode) {
        case 'UNIT':
            return price * quantity;

        case 'UNIT_PRICED_BY_WEIGHT': {
            const avgWeight = product.weightEstimate?.averageWeight;
            if (!avgWeight) return null;
            return price * avgWeight * quantity;
        }

        case 'WEIGHT':
            return price * quantity;

        case 'WEIGHT_ESTIMATED': {
            const gramsPerPerson = product.servingEstimate?.gramsPerPerson;
            if (!gramsPerPerson) return null;
            const weightKg = (gramsPerPerson * servings) / 1000;
            return price * weightKg;
        }

        case 'WEIGHT_PACKAGED': {
            const portionPrice = getPortionPrice(product, selectedPortionSizeId ?? undefined);
            if (portionPrice === null) return null;
            return portionPrice * quantity;
        }

        default:
            return null;
    }
}

/**
 * Get the quantity value to store in the order line based on sale mode
 * For WEIGHT_ESTIMATED, converts servings to kg
 */
export function getOrderLineQuantity(
    product: Product,
    saleMode: SaleMode,
    quantity: number,
    servings: number
): number {
    switch (saleMode) {
        case 'WEIGHT_ESTIMATED': {
            const gramsPerPerson = product.servingEstimate?.gramsPerPerson;
            if (!gramsPerPerson) return quantity;
            return (gramsPerPerson * servings) / 1000; // Convert to kg
        }
        default:
            return quantity;
    }
}
